import React, { useEffect, useRef, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, UserPlusIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  assignManager,
  fetchManagers,
  fetchOrgMembers,
  revokeManager,
  selectCanManageScimManagers,
  selectManagers,
  selectOrgMembers,
  selectPermissions,
} from '../reducers/scimManagementSlice';
import type { IOrgMember } from '../reducers/scimManagementSlice';

const DEBOUNCE_MS = 300;

const ScimManagerList = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { user: authUser } = useAuth0();
  const managers = useAppSelector(selectManagers);
  const orgMembers = useAppSelector(selectOrgMembers);
  const canManageScimManagers = useAppSelector(selectCanManageScimManagers);
  const permissions = useAppSelector(selectPermissions);
  const isSuperuser = permissions?.is_superuser ?? false;

  const [selectedMember, setSelectedMember] = useState<IOrgMember | null>(null);
  const [emailMode, setEmailMode] = useState(false);
  const [emailInput, setEmailInput] = useState('');

  const [revokeConfirmMemberId, setRevokeConfirmMemberId] = useState<number | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [orgIdInput, setOrgIdInput] = useState('');

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    void dispatch(fetchManagers());
    void dispatch(fetchOrgMembers());
  }, [dispatch]);

  const handleQueryChange = (value: string): void => {
    if (debounceTimer.current !== null) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      void dispatch(fetchOrgMembers(value));
    }, DEBOUNCE_MS);
  };

  const handleAssign = (): void => {
    setAssignError(null);
    const organisationId = isSuperuser && orgIdInput.trim() !== '' ? parseInt(orgIdInput.trim(), 10) : undefined;
    if (emailMode) {
      if (emailInput.trim() !== '') {
        void dispatch(assignManager({ email: emailInput.trim(), organisationId })).then((result) => {
          if (assignManager.rejected.match(result)) {
            const payload = result.payload as { message?: string } | string | undefined;
            const msg =
              typeof payload === 'object' && payload !== null && 'message' in payload
                ? payload.message
                : typeof payload === 'string'
                ? payload
                : 'Failed to assign manager';
            setAssignError(msg ?? 'Failed to assign manager');
          } else {
            setEmailInput('');
          }
        });
      }
    } else {
      if (selectedMember !== null) {
        void dispatch(assignManager({ memberId: selectedMember.id, organisationId })).then((result) => {
          if (assignManager.rejected.match(result)) {
            const payload = result.payload as { message?: string } | string | undefined;
            const msg =
              typeof payload === 'object' && payload !== null && 'message' in payload
                ? payload.message
                : typeof payload === 'string'
                ? payload
                : 'Failed to assign manager';
            setAssignError(msg ?? 'Failed to assign manager');
          } else {
            setSelectedMember(null);
            void dispatch(fetchOrgMembers());
          }
        });
      }
    }
  };

  const handleRevokeConfirmed = (): void => {
    if (revokeConfirmMemberId === null) return;
    setRevokeError(null);
    void dispatch(revokeManager(revokeConfirmMemberId)).then((result) => {
      if (revokeManager.rejected.match(result)) {
        const payload = result.payload as { message?: string } | string | undefined;
        const msg =
          typeof payload === 'object' && payload !== null && 'message' in payload
            ? payload.message
            : typeof payload === 'string'
            ? payload
            : 'Failed to revoke manager';
        setRevokeError(msg ?? 'Failed to revoke manager');
      } else {
        setRevokeConfirmMemberId(null);
      }
    });
  };

  const revokeTarget =
    revokeConfirmMemberId !== null ? managers.find((m) => m.member_id === revokeConfirmMemberId) : null;
  const isSelfRevoke = revokeTarget !== null && revokeTarget !== undefined && revokeTarget.email === authUser?.email;

  return (
    <div className="rounded-md shadow-sm bg-white">
      <div className="px-4 py-3 border-b border-gray-200">
        <h2 className="text-dataops-dark text-base">SCIM Managers</h2>
        <p className="text-secondary-text text-sm mt-0.5">
          Members with the SCIM Manager role can manage SCIM groups for their organisation.
        </p>
      </div>

      {revokeError !== null && (
        <div className="mx-4 mt-3 rounded-md bg-red-50 border border-red-highlight px-4 py-3 text-sm text-red-highlight flex items-center justify-between gap-2">
          <span>{revokeError}</span>
          <button type="button" onClick={() => setRevokeError(null)} aria-label="Dismiss error">
            <XCircleIcon className="h-4 w-4 shrink-0" />
          </button>
        </div>
      )}

      {assignError !== null && (
        <div className="mx-4 mt-3 rounded-md bg-red-50 border border-red-highlight px-4 py-3 text-sm text-red-highlight flex items-center justify-between gap-2">
          <span>{assignError}</span>
          <button type="button" onClick={() => setAssignError(null)} aria-label="Dismiss error">
            <XCircleIcon className="h-4 w-4 shrink-0" />
          </button>
        </div>
      )}

      <div className="overflow-hidden">
        <table className="w-full divide-y divide-gray-300 table-fixed text-sm">
          <thead>
            <tr className="bg-dataops-dark divide-x divide-white">
              <th className="w-6/12 py-3 pl-4 pr-3 text-left text-white">Email</th>
              <th className="w-5/12 px-3 py-3 text-left text-white">Name</th>
              <th className="w-1/12 px-3 py-3 text-white" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {managers.map((m) => (
              <tr key={m.member_id} className="even:bg-gray-50">
                <td className="py-3 pl-4 pr-3 text-primary-text truncate">{m.email}</td>
                <td className="px-3 py-3 text-secondary-text truncate">
                  {m.name ?? <span className="text-gray-400 italic">—</span>}
                </td>
                <td className="px-3 py-3">
                  {canManageScimManagers && (
                    <button
                      onClick={() => {
                        setRevokeError(null);
                        setRevokeConfirmMemberId(m.member_id);
                      }}
                      aria-label={`Revoke SCIM Manager from ${m.email}`}
                    >
                      <XCircleIcon className="h-5 w-5 text-red-highlight hover:text-red-700" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {managers.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-4 text-sm text-gray-600">
                  No SCIM Managers assigned
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {canManageScimManagers && (
        <div className="px-4 py-3 border-t border-gray-200 space-y-2">
          {isSuperuser && (
            <div className="flex items-center gap-2">
              <label htmlFor="scim-org-id" className="text-sm text-secondary-text whitespace-nowrap">
                Organisation ID
              </label>
              <input
                id="scim-org-id"
                type="number"
                placeholder="Assign for org…"
                value={orgIdInput}
                onChange={(e) => setOrgIdInput(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm w-40"
                aria-label="Organisation ID to assign manager for"
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setEmailMode(false);
                setEmailInput('');
                setAssignError(null);
                if (debounceTimer.current !== null) {
                  clearTimeout(debounceTimer.current);
                }
                void dispatch(fetchOrgMembers());
              }}
              className={`text-sm px-2 py-1 rounded ${
                !emailMode ? 'bg-dataops-primary-blue text-white' : 'text-secondary-text hover:text-primary-text'
              }`}
            >
              Pick from list
            </button>
            <button
              type="button"
              onClick={() => {
                setEmailMode(true);
                setSelectedMember(null);
                setAssignError(null);
                if (debounceTimer.current !== null) {
                  clearTimeout(debounceTimer.current);
                }
                void dispatch(fetchOrgMembers());
              }}
              className={`text-sm px-2 py-1 rounded ${
                emailMode ? 'bg-dataops-primary-blue text-white' : 'text-secondary-text hover:text-primary-text'
              }`}
            >
              Enter email
            </button>
          </div>

          <div className="flex items-center gap-2">
            {emailMode ? (
              <input
                type="email"
                placeholder="Email address"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setAssignError(null);
                }}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm w-64"
                aria-label="Email address to assign"
              />
            ) : (
              <Combobox value={selectedMember} onChange={setSelectedMember} nullable>
                <div className="relative w-64">
                  <Combobox.Input
                    className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full pr-8"
                    placeholder="Search members…"
                    displayValue={(m: IOrgMember | null) => m?.email ?? ''}
                    onChange={(e) => handleQueryChange(e.target.value)}
                    aria-label="Search members to assign"
                  />
                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  </Combobox.Button>
                  <Combobox.Options className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-md bg-white shadow-lg border border-gray-200 text-sm">
                    {orgMembers.length === 0 ? (
                      <div className="px-3 py-2 text-secondary-text">No members found</div>
                    ) : (
                      orgMembers.map((m) => (
                        <Combobox.Option
                          key={m.id}
                          value={m}
                          className={({ active }) =>
                            `cursor-pointer select-none px-3 py-2 flex items-center gap-2 ${
                              active ? 'bg-dataops-primary-blue text-white' : 'text-primary-text'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              {selected ? <CheckIcon className="h-4 w-4 shrink-0" /> : <span className="w-4" />}
                              <span className="truncate">{m.email}</span>
                              {m.is_scim_manager && (
                                <span className="ml-auto text-xs text-secondary-text whitespace-nowrap">manager</span>
                              )}
                            </>
                          )}
                        </Combobox.Option>
                      ))
                    )}
                  </Combobox.Options>
                </div>
              </Combobox>
            )}

            <button
              onClick={handleAssign}
              className="flex items-center gap-1 bg-dataops-primary-blue hover:bg-hover-primary-blue text-white rounded px-3 py-1.5 text-sm"
            >
              <UserPlusIcon className="h-4 w-4" />
              Assign Manager
            </button>
          </div>
        </div>
      )}

      {/* Revoke confirm dialog */}
      {revokeConfirmMemberId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6 space-y-4">
            <h3 className="text-dataops-dark text-base">Revoke SCIM Manager</h3>
            <p className="text-sm text-secondary-text">
              {isSelfRevoke
                ? "This will remove your SCIM Manager privileges — you won't be able to re-grant them yourself."
                : `Remove SCIM Manager role from ${revokeTarget?.email ?? ''}?`}
            </p>
            {revokeError !== null && <p className="text-sm text-red-highlight">{revokeError}</p>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setRevokeConfirmMemberId(null);
                  setRevokeError(null);
                }}
                className="px-3 py-1.5 text-sm rounded border border-gray-300 text-secondary-text hover:text-primary-text"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRevokeConfirmed}
                className="px-3 py-1.5 text-sm rounded bg-red-highlight hover:bg-red-700 text-white"
              >
                Revoke
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScimManagerList;
